/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Pressable, Icon } from '@components/atoms'
import {
	useAppSelector,
	useAppDispatch,
	configActions,
	ConfigStateType,
} from '@redux/index'
// 결합 로고(원형 사자 + Judahs) — 텍스트만으로 로고를 만들지 않고, 마크와 텍스트를 따로 놓지도 않는다(DESIGN_SYSTEM §13.3, 2026-09-28)
import logoTitlePng from '@public/images/logo/logo_title.png'
// 접힌 사이드바(64)는 마크만
import logoPng from '@public/images/logo/logo_black.png'
import { mono, boSize, boAdmin } from '@components/systems/backoffice/backoffice.tokens'
import { useNavigate } from 'react-router-dom'
import { useDrawerOpen } from '@components/systems/backoffice/BoShellContext'

/**
 * 사이드바 머리.
 * 관리자 모드면 맨 위에 **관리자 모드 칸**(높이 48, 잉크 — 우측 상단 바와 이어져 한 띠)이 들어가고
 * 로고 행은 그만큼 아래로 밀린다. 칸 = 나가기(×, 좌) · 관리자 모드(가운데, 볼드). 접힌 사이드바에서는 × 만.
 * 그 아래는 결합 로고 이미지 `logo_title.png`(펼침) / 원형 마크(접힘) — 두 모드가 같다(DESIGN_SYSTEM §13.3).
 */
export const DrawerHeader = () => {
	// hook
	const navigate = useNavigate()

	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// redux state
	const systemName: string = useAppSelector(state => state.config.systemName)
	const mode: ConfigStateType['mode'] = useAppSelector(state => state.config.mode)
	const isOpenDrawer: boolean = useDrawerOpen()

	const onTurnOffAdminClick = React.useCallback(() => {
		reduxDispatch(configActions.MODE('main'))
	}, [])

	const onGoHomeButton = () => {
		const homePath: string = mode === 'admin' ? '/admin/home' : '/main/home'
		navigate(homePath)
	}

	return (
		<View css={style} className={isOpenDrawer ? 'open' : 'closed'}>
			{mode === 'admin' ? (
				<View className="adminBand">
					<Pressable
						className="adminClose"
						onClick={onTurnOffAdminClick}
						aria-label="관리자 모드 나가기"
					>
						<Icon name="close" className="adminCloseIcon" />
					</Pressable>
					{isOpenDrawer ? <span className="adminLabel">관리자 모드</span> : null}
				</View>
			) : null}

			<Pressable className="homeButton" onClick={onGoHomeButton} aria-label="홈">
				{isOpenDrawer ? (
					<img src={logoTitlePng} className="logoTitle" alt={systemName} />
				) : (
					<img src={logoPng} className="logo" alt={systemName} />
				)}
			</Pressable>
		</View>
	)
}

const style = css({
	flexShrink: 0,
	borderBottom: `1px solid ${mono.line}`,

	'.adminBand': {
		display: 'flex',
		alignItems: 'center',
		gap: 10,
		height: boSize.navH,
		padding: '0 18px',
		background: boAdmin.bar,
		boxSizing: 'border-box',

		'.adminLabel': {
			flex: 1,
			// × 폭(22)만큼 오른쪽을 비워 글자가 칸 정중앙에 온다
			paddingRight: 22,
			textAlign: 'center',
			fontSize: 13,
			fontWeight: 700,
			color: boAdmin.textOn,
			whiteSpace: 'nowrap',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
		},
		'.adminClose': {
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			flexShrink: 0,
			width: 22,
			height: 22,
			padding: 0,
			borderRadius: 2,
			transition: 'background-color 200ms ease-out',
			'.adminCloseIcon': { fontSize: 16, color: boAdmin.text },
			'&:hover': { background: boAdmin.barHover },
			'&:hover .adminCloseIcon': { color: boAdmin.textOn },
		},
	},

	'&.closed .adminBand': {
		justifyContent: 'center',
		padding: 0,
	},

	'.homeButton': {
		display: 'flex',
		alignItems: 'center',
		gap: 12,
		width: '100%',
		height: boSize.navH,
		padding: '0 18px',
		textAlign: 'left',

		'.logoTitle': {
			display: 'block',
			height: 24,
			width: 'auto',
			maxWidth: '100%',
			objectFit: 'contain',
		},
		'.logo': {
			display: 'block',
			flexShrink: 0,
			width: 28,
			height: 28,
			objectFit: 'contain',
		},
	},

	'&.closed .homeButton': {
		justifyContent: 'center',
		padding: 0,
	},
})
